import React, { useState } from 'react';
import { Auction } from '../../types/auction.types';
import {
  DEFAULT_SOFT_CLOSE_WINDOW_SECONDS,
  DEFAULT_SOFT_CLOSE_EXTENSION_SECONDS,
  DEFAULT_MAX_EXTENSION_SECONDS,
} from '../../utils/lotTiming';

export interface GoLiveSettings {
  pushToAuctionRinger: boolean;
  uploadToAuctionZip: boolean;
  auctionZipUsername?: string;
  auctionZipPassword?: string;
  auctionZipCategories?: string[];
  
  // NEW: Timing settings
  staggerTime: number; // Seconds between lot closes
  softCloseWindow: number; // Seconds before end to trigger extension
  softCloseExtension: number; // Seconds to add when extended
  maxExtensionSeconds: number; // Hard backstop: no lot closes later than scheduled + this
  coolOffWindow: number; // Seconds after the last lot closes for tallying/confirming (T&Cs)
}

interface GoLiveDialogProps {
  auction: Auction;
  onClose: () => void;
  onGoLive: (settings: GoLiveSettings) => Promise<void>;
}

const AUCTIONZIP_CATEGORIES = [
  'Antiques-Household-Collectibles',
  'Agricultural-Farm',
  'Automobiles',
  'Benefit-Charity',
  'Commercial-Industrial',
  'Heavy Equipment',
  'New Merchandise',
  'Online Bidding',
  'Storage Auctions',
  'Wholesale Auctions',
  'Real Estate - General',
  'Real Estate - Land',
  'Real Estate - Commercial',
  'Real Estate - Residential',
  'Real Estate - Farm',
];

export const GoLiveDialog: React.FC<GoLiveDialogProps> = ({
  auction,
  onClose,
  onGoLive,
}) => {
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Settings state
  const [settings, setSettings] = useState<GoLiveSettings>({
    pushToAuctionRinger: true,
    uploadToAuctionZip: false,
    auctionZipUsername: '',
    auctionZipPassword: '',
    auctionZipCategories: [],
    
    // Default timing values (HiBid-style)
    staggerTime: 8, // 8 seconds between lots (HiBid default)
    softCloseWindow: DEFAULT_SOFT_CLOSE_WINDOW_SECONDS,
    softCloseExtension: DEFAULT_SOFT_CLOSE_EXTENSION_SECONDS,
    maxExtensionSeconds: DEFAULT_MAX_EXTENSION_SECONDS,
    coolOffWindow: 3600, // 1 hour tally/confirm window after the last lot closes
  });

  // Validation checks
  const hasLots = auction.lots && auction.lots.length > 0;
  const hasDates = auction.startDate && auction.endDate;
  const datesValid = auction.startDate && auction.endDate && auction.endDate > auction.startDate;
  const hasDescription = !!auction.description;
  const hasLocation = !!auction.location;

  const allValidationsPassed = hasLots && hasDates && datesValid && hasDescription && hasLocation;

  const handleGoLive = async () => {
    setProcessing(true);
    setError(null);

    try {
      await onGoLive(settings);
      setStep(5); // Success step
    } catch (err) {
      console.error('Go live error:', err);
      setError(err instanceof Error ? err.message : 'Failed to go live');
    } finally {
      setProcessing(false);
    }
  };

  const toggleAuctionZipCategory = (category: string) => {
    setSettings(prev => ({
      ...prev,
      auctionZipCategories: prev.auctionZipCategories?.includes(category)
        ? prev.auctionZipCategories.filter(c => c !== category)
        : [...(prev.auctionZipCategories || []), category],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Configure & Go Live</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={processing}
            >
              ×
            </button>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-between mb-8">
            <div className={`flex items-center ${step >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
              <span className="font-bold">1</span>
              <span className="ml-2 text-sm">Validation</span>
            </div>
            <div className="flex-1 h-1 mx-2 bg-gray-200 rounded">
              <div className={`h-full rounded transition-all ${step >= 2 ? 'bg-primary-600 w-full' : 'bg-gray-200 w-0'}`}></div>
            </div>
            <div className={`flex items-center ${step >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
              <span className="font-bold">2</span>
              <span className="ml-2 text-sm">Timing</span>
            </div>
            <div className="flex-1 h-1 mx-2 bg-gray-200 rounded">
              <div className={`h-full rounded transition-all ${step >= 3 ? 'bg-primary-600 w-full' : 'bg-gray-200 w-0'}`}></div>
            </div>
            <div className={`flex items-center ${step >= 3 ? 'text-primary-600' : 'text-gray-400'}`}>
              <span className="font-bold">3</span>
              <span className="ml-2 text-sm">Platforms</span>
            </div>
            <div className="flex-1 h-1 mx-2 bg-gray-200 rounded">
              <div className={`h-full rounded transition-all ${step >= 4 ? 'bg-primary-600 w-full' : 'bg-gray-200 w-0'}`}></div>
            </div>
            <div className={`flex items-center ${step >= 4 ? 'text-primary-600' : 'text-gray-400'}`}>
              <span className="font-bold">4</span>
              <span className="ml-2 text-sm">Review</span>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}

          {/* Step 1: Validation */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold mb-4">Pre-Flight Checks</h3>

              <div className="space-y-3">
                <div className={`flex items-center gap-2 ${hasLots ? 'text-green-600' : 'text-red-600'}`}>
                  <span>{hasLots ? '✓' : '✗'}</span>
                  <span>Auction has lots ({auction.lots?.length || 0})</span>
                </div>

                <div className={`flex items-center gap-2 ${hasDates ? 'text-green-600' : 'text-red-600'}`}>
                  <span>{hasDates ? '✓' : '✗'}</span>
                  <span>Start and end dates set</span>
                </div>

                <div className={`flex items-center gap-2 ${datesValid ? 'text-green-600' : 'text-red-600'}`}>
                  <span>{datesValid ? '✓' : '✗'}</span>
                  <span>End date is after start date</span>
                </div>

                <div className={`flex items-center gap-2 ${hasDescription ? 'text-green-600' : 'text-red-600'}`}>
                  <span>{hasDescription ? '✓' : '✗'}</span>
                  <span>Description provided</span>
                </div>

                <div className={`flex items-center gap-2 ${hasLocation ? 'text-green-600' : 'text-red-600'}`}>
                  <span>{hasLocation ? '✓' : '✗'}</span>
                  <span>Location provided</span>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={onClose} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!allValidationsPassed}
                  className="btn-primary"
                >
                  Next: Configure Timing
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Timing Configuration */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold mb-4">Lot Closing Timing</h3>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>How it works:</strong> Lots close in sequence with a stagger time between each.
                  When bids come in during the final minutes (soft close window), the lot's closing time
                  is automatically extended.
                </p>
              </div>

              {/* Stagger Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Stagger Time Between Lots
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="1"
                    max="300"
                    value={settings.staggerTime}
                    onChange={(e) => setSettings(prev => ({ ...prev, staggerTime: parseInt(e.target.value) || 8 }))}
                    className="input w-32"
                  />
                  <span className="text-gray-600">seconds</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Time between each lot closing. HiBid default: 8 seconds. WhiteLabelBuddy default: 60 seconds.
                </p>
              </div>

              {/* Soft Close Window */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Soft Close Window
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="60"
                    max="1800"
                    step="60"
                    value={settings.softCloseWindow}
                    onChange={(e) => setSettings(prev => ({ ...prev, softCloseWindow: parseInt(e.target.value) || DEFAULT_SOFT_CLOSE_WINDOW_SECONDS }))}
                    className="input w-32"
                  />
                  <span className="text-gray-600">seconds ({Math.floor(settings.softCloseWindow / 60)} minutes)</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Bids placed within this time before closing will trigger an extension. Default: 2 minutes.
                </p>
              </div>

              {/* Extension Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Extension Time
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="15"
                    max="300"
                    step="15"
                    value={settings.softCloseExtension}
                    onChange={(e) => setSettings(prev => ({ ...prev, softCloseExtension: parseInt(e.target.value) || DEFAULT_SOFT_CLOSE_EXTENSION_SECONDS }))}
                    className="input w-32"
                  />
                  <span className="text-gray-600">seconds ({Math.floor(settings.softCloseExtension / 60)} minutes)</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  How much time to add when a bid comes in during soft close. Default: 60 seconds.
                </p>
              </div>

              {/* Cool-off / tally window — auctioneer-defined, stated in the sale T&Cs. */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cool-Off / Tally Window
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="0"
                    step="300"
                    value={settings.coolOffWindow}
                    onChange={(e) => setSettings(prev => ({ ...prev, coolOffWindow: parseInt(e.target.value) || 0 }))}
                    className="input w-32"
                  />
                  <span className="text-gray-600">seconds ({Math.floor(settings.coolOffWindow / 60)} minutes)</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  After the last lot closes, bids are tallied and confirmed for this long before results are
                  official. Shown to bidders on the homepage and the Sale Info page. Default: 3600 (1 hour).
                </p>
              </div>

              {/* Example Preview */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm font-semibold text-gray-700 mb-2">Example Timeline:</p>
                <p className="text-xs text-gray-600">
                  If auction ends at 8:00 PM with {auction.lots?.length || 0} lots and {settings.staggerTime} second stagger:
                </p>
                <ul className="text-xs text-gray-600 mt-2 space-y-1">
                  <li>• Lot 1 closes at 8:00:00 PM</li>
                  <li>• Lot 2 closes at 8:00:{settings.staggerTime.toString().padStart(2, '0')} PM</li>
                  <li>• Lot 3 closes at 8:00:{(settings.staggerTime * 2).toString().padStart(2, '0')} PM</li>
                  <li>• Last lot closes at ~{new Date(auction.endDate.getTime() + (auction.lots?.length || 0) * settings.staggerTime * 1000).toLocaleTimeString()}</li>
                </ul>
              </div>

              <div className="flex justify-between gap-3 mt-6">
                <button onClick={() => setStep(1)} className="btn-secondary">
                  Back
                </button>
                <button onClick={() => setStep(3)} className="btn-primary">
                  Next: Platform Configuration
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Platform Configuration */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold mb-4">Platform Configuration</h3>

              {/* AuctionRinger */}
              <div className="p-4 bg-green-50 border-2 border-green-500 rounded-lg">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-5 h-5"
                  />
                  <div>
                    <p className="font-bold text-green-800">Push to AuctionRinger</p>
                    <p className="text-sm text-green-700">
                      Required - Your auction will appear on {auction.auctionHouseName}.AuctionRinger.com
                    </p>
                  </div>
                </div>
              </div>

              {/* AuctionZip */}
              <div className={`p-4 border-2 rounded-lg ${settings.uploadToAuctionZip ? 'bg-blue-50 border-blue-500' : 'bg-gray-50 border-gray-300'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    checked={settings.uploadToAuctionZip}
                    onChange={(e) => setSettings(prev => ({ ...prev, uploadToAuctionZip: e.target.checked }))}
                    className="w-5 h-5"
                  />
                  <div>
                    <p className="font-bold">Upload to AuctionZip</p>
                    <p className="text-sm text-gray-600">
                      Optional - Increase visibility by listing on AuctionZip.com
                    </p>
                  </div>
                </div>

                {settings.uploadToAuctionZip && (
                  <div className="ml-8 space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Username</label>
                      <input
                        type="text"
                        value={settings.auctionZipUsername}
                        onChange={(e) => setSettings(prev => ({ ...prev, auctionZipUsername: e.target.value }))}
                        className="input"
                        placeholder="Your AuctionZip username"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Password</label>
                      <input
                        type="password"
                        value={settings.auctionZipPassword}
                        onChange={(e) => setSettings(prev => ({ ...prev, auctionZipPassword: e.target.value }))}
                        className="input"
                        placeholder="Your AuctionZip password"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Categories (select all that apply)</label>
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-white rounded border">
                        {AUCTIONZIP_CATEGORIES.map(cat => (
                          <label key={cat} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={settings.auctionZipCategories?.includes(cat)}
                              onChange={() => toggleAuctionZipCategory(cat)}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{cat}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between gap-3 mt-6">
                <button onClick={() => setStep(2)} className="btn-secondary">
                  Back
                </button>
                <button onClick={() => setStep(4)} className="btn-primary">
                  Next: Review & Confirm
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold mb-4">Review & Confirm</h3>

              <div className="p-4 bg-yellow-50 border border-yellow-500 rounded-lg">
                <p className="text-yellow-800 font-semibold mb-2">
                  ⚠️ Warning: This action cannot be undone
                </p>
                <p className="text-sm text-yellow-700">
                  Once you go live, bidders will be able to place bids on your lots.
                  Make sure everything is configured correctly.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Auction</p>
                  <p className="text-lg">{auction.title}</p>
                  <p className="text-sm text-gray-600">{auction.lots?.length || 0} lots</p>
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-700">Timing Configuration</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Stagger: {settings.staggerTime} seconds between lots</li>
                    <li>• Soft Close Window: {Math.floor(settings.softCloseWindow / 60)} minutes</li>
                    <li>• Extension Time: {settings.softCloseExtension} seconds</li>
                  </ul>
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-700">Platforms</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• AuctionRinger: Yes (always enabled)</li>
                    <li>• AuctionZip: {settings.uploadToAuctionZip ? 'Yes' : 'No'}</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-between gap-3 mt-6">
                <button onClick={() => setStep(3)} className="btn-secondary" disabled={processing}>
                  Back
                </button>
                <button
                  onClick={handleGoLive}
                  disabled={processing}
                  className="btn-primary bg-green-600 hover:bg-green-700"
                >
                  {processing ? '⏳ Going Live...' : '🚀 Go Live Now!'}
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {step === 5 && (
            <div className="text-center py-8 space-y-6">
              <div className="text-6xl">🎉</div>
              <h3 className="text-2xl font-bold text-green-600">Auction is Live!</h3>
              <p className="text-gray-600">
                Your auction is now accepting bids. Lots will close sequentially with {settings.staggerTime} second intervals.
              </p>

              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">View Your Auction:</p>
                <p className="text-sm text-gray-600">
                  AuctionRinger: {auction.auctionHouseName}.AuctionRinger.com
                </p>
                {settings.uploadToAuctionZip && (
                  <p className="text-sm text-gray-600">
                    AuctionZip: Check your AuctionZip dashboard
                  </p>
                )}
              </div>

              <button onClick={onClose} className="btn-primary">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};